import mergeClassNames from "common/mergeClassNames";
import React, { PropsWithChildren } from "react";

interface ISegmentProps {
    shadow?: boolean;
    border?: boolean;
    rounded?: boolean;
    children?: React.ReactNode;
    className?: string;
    raised?: boolean;
    stacked?: boolean;
    compact?: boolean;
    color?: "red" | "orange" | "yellow" | "olive" | "green" | "teal" | "blue" | "violet" | "purple" | "pink" | "brown" | "grey" | "black";
    inverted?: boolean;
    attached?: boolean | "top" | "bottom";
    padded?: boolean | "very";
    loading?: boolean;
    disabled?: boolean;
    secondary?: boolean;
    tertiary?: boolean;
    basic?: boolean;
    placeholder?: boolean;
}

interface ISegmentGroupProps {
    children: React.ReactNode;
    className?: string;
    horizontal?: boolean;
    raised?: boolean;
    stacked?: boolean;
    compact?: boolean;
}

const COLOR_STYLES: Record<string, string> = {
    red: "border-t-[#db2828] border-t-2",
    orange: "border-t-[#f2711c] border-t-2",
    yellow: "border-t-[#fbbd08] border-t-2",
    olive: "border-t-[#b5cc18] border-t-2",
    green: "border-t-[#21ba45] border-t-2",
    teal: "border-t-[#00b5ad] border-t-2",
    blue: "border-t-[#2185d0] border-t-2",
    violet: "border-t-[#6435c9] border-t-2",
    purple: "border-t-[#a333c8] border-t-2",
    pink: "border-t-[#e03997] border-t-2",
    brown: "border-t-[#a5673f] border-t-2",
    grey: "border-t-[#767676] border-t-2",
    black: "border-t-[#1b1c1d] border-t-2",
};

function SegmentGroup({ children, className, horizontal, raised, stacked, compact }: ISegmentGroupProps) {
    const groupStyles = mergeClassNames(
        "border border-solid border-[rgba(34,36,38,.15)] rounded-[0.28571429rem] bg-white",
        horizontal ? "flex flex-row" : "flex flex-col",
        raised && "shadow-[0_2px_4px_0_rgba(34,36,38,.12),0_2px_10px_0_rgba(34,36,38,.15)]",
        stacked && "relative",
        compact && "inline-flex",
        "[&>*]:border-0 [&>*]:rounded-none [&>*:first-child]:rounded-t-[0.28571429rem] [&>*:last-child]:rounded-b-[0.28571429rem] [&>*:not(:first-child)]:border-t [&>*:not(:first-child)]:border-solid [&>*:not(:first-child)]:border-t-[rgba(34,36,38,.15)]",
        horizontal &&
            "[&>*]:flex-1 [&>*:not(:first-child)]:border-t-0 [&>*:not(:first-child)]:border-l [&>*:not(:first-child)]:border-solid [&>*:not(:first-child)]:border-l-[rgba(34,36,38,.15)]",
        className,
    );

    return (
        <div data-testid="segment-group" className={groupStyles}>
            {children}
        </div>
    );
}

function Segment({
    children,
    shadow = false,
    className,
    border = false,
    rounded = true,
    raised,
    stacked,
    compact,
    color,
    inverted,
    attached,
    padded,
    loading,
    disabled,
    secondary,
    tertiary,
    basic,
    placeholder,
}: ISegmentProps) {
    const segmentStyles = mergeClassNames(
        "relative size-full flex flex-col flex-1 bg-white p-4 font-sans text-[1rem] [&>*:last-child]:mb-0",
        rounded && "rounded-md",
        border && "border border-dashed border-[rgba(34,36,38,.15)]",
        raised && "shadow-[0_2px_4px_0_rgba(34,36,38,.12),0_2px_10px_0_rgba(34,36,38,.15)]",
        shadow && "shadow-md",
        stacked && "pb-[1.4em]",
        compact && "inline-block",
        color && COLOR_STYLES[color],
        inverted && "bg-[#1b1c1d] text-white border-none",
        attached === true && "rounded-none my-0",
        attached === "top" && "rounded-b-none mb-0 border-b-0",
        attached === "bottom" && "rounded-t-none mt-0 border-t-0",
        padded === true && "p-[1.5em]",
        padded === "very" && "p-[3em]",
        loading && "min-h-[6em] pointer-events-none",
        disabled && "opacity-45",
        secondary && "bg-[#f3f4f5]",
        tertiary && "bg-[#dcddde]",
        basic && "bg-transparent border-none shadow-none",
        placeholder && "min-h-[18rem] items-center justify-center bg-[#f9fafb]",
        className,
    );

    return (
        <div data-testid="segment" className={segmentStyles}>
            {loading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] bg-white/80">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#767676] border-t-transparent"></div>
                </div>
            )}
            {children}
        </div>
    );
}

Segment.Group = SegmentGroup;

function SegmentContainer({ children }: PropsWithChildren) {
    React.useEffect(() => {
        const pageContent = document.getElementsByClassName("page-container")[0];
        if (pageContent) {
            pageContent.classList.add("bg-gray-100");
        }
    }, []);

    return (
        <div data-testid="segment-container" className="relative flex size-full flex-1 flex-col gap-4 overflow-auto px-1 pb-2">
            {children}
        </div>
    );
}

export default Segment;
export { Segment, SegmentContainer, SegmentGroup };
